import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Pencil, Save, Search, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { translations as defaults } from "../../i18n/translations";
import { useLang } from "../../contexts/LanguageContext";
import PageHead from "../../components/PageHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function flattenObj(obj, prefix = "") {
  const result = {};
  for (const key of Object.keys(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (obj[key] && typeof obj[key] === "object" && !Array.isArray(obj[key])) {
      Object.assign(result, flattenObj(obj[key], path));
    } else {
      result[path] = obj[key];
    }
  }
  return result;
}

function setNestedValue(obj, path, value) {
  const keys = path.split(".");
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]] || typeof current[keys[i]] !== "object") current[keys[i]] = {};
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
}

function getNestedValue(obj, path) {
  const keys = path.split(".");
  let val = obj;
  for (const k of keys) {
    if (val && typeof val === "object" && k in val) val = val[k];
    else return undefined;
  }
  return val;
}

const SECTIONS = [
  { key: "nav", label: "Navigation" },
  { key: "footer", label: "Footer" },
  { key: "home", label: "Homepage" },
  { key: "solutions", label: "Solutions" },
  { key: "engagements", label: "Engagements" },
  { key: "contact", label: "Contact" },
  { key: "casReels", label: "Cas reels / Case studies" },
  { key: "login", label: "Login / Register" },
  { key: "dashboard", label: "Dashboard" },
];

export default function EditorPage() {
  const [customData, setCustomData] = useState({ fr: {}, en: {} });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState({ home: true });
  const [editLang, setEditLang] = useState("fr");
  const { reloadTranslations } = useLang();

  useEffect(() => {
    axios.get(`${API}/translations`, { withCredentials: true }).then(({ data }) => {
      if (data && (data.fr || data.en)) setCustomData({ fr: data.fr || {}, en: data.en || {} });
    }).catch(() => {});
  }, []);

  const flatDefaults = useMemo(() => flattenObj(defaults[editLang]), [editLang]);
  const flatCustom = useMemo(() => flattenObj(customData[editLang] || {}), [customData, editLang]);

  const allKeys = useMemo(() => {
    return Object.keys(flatDefaults).filter((k) => {
      const val = flatDefaults[k];
      return typeof val === "string";
    });
  }, [flatDefaults]);

  const filteredKeys = useMemo(() => {
    if (!search) return allKeys;
    const q = search.toLowerCase();
    return allKeys.filter((k) => {
      const defaultVal = String(flatDefaults[k] || "").toLowerCase();
      const customVal = String(flatCustom[k] || "").toLowerCase();
      return k.toLowerCase().includes(q) || defaultVal.includes(q) || customVal.includes(q);
    });
  }, [allKeys, search, flatDefaults, flatCustom]);

  const groupedKeys = useMemo(() => {
    const groups = {};
    for (const key of filteredKeys) {
      const section = key.split(".")[0];
      if (!groups[section]) groups[section] = [];
      groups[section].push(key);
    }
    return groups;
  }, [filteredKeys]);

  const handleChange = (key, value) => {
    setCustomData((prev) => {
      const next = { ...prev, [editLang]: { ...(prev[editLang] || {}) } };
      const langObj = JSON.parse(JSON.stringify(next[editLang]));
      const defaultVal = getNestedValue(defaults[editLang], key);
      if (value === "" || value === defaultVal) {
        // Remove override
        const keys = key.split(".");
        let current = langObj;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) break;
          current = current[keys[i]];
        }
        if (current) delete current[keys[keys.length - 1]];
      } else {
        setNestedValue(langObj, key, value);
      }
      next[editLang] = langObj;
      return next;
    });
  };

  const handleReset = (key) => {
    handleChange(key, "");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/translations`, customData, { withCredentials: true });
      toast.success("Textes sauvegardes !");
      await reloadTranslations();
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const modifiedCount = Object.keys(flatCustom).filter((k) => flatCustom[k] && flatCustom[k] !== "").length;

  return (
    <div data-testid="editor-page">
      <PageHead title="Editeur de textes" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: "'Playfair Display'" }}>Editeur de textes</h1>
          <p className="text-sm font-light mt-1" style={{ color: "var(--kyra-mu)" }}>Modifiez les textes du site en temps reel</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="text-xs px-3 py-1" style={{ background: "rgba(34,197,94,0.1)", color: "var(--kyra-gm)", border: "none" }}>
            <Pencil className="w-3 h-3 mr-1.5" /> {modifiedCount} modifie(s)
          </Badge>
          <button
            data-testid="save-translations-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 text-xs font-medium text-white rounded-lg transition-colors cursor-pointer disabled:opacity-50"
            style={{ background: "var(--kyra-gr)", border: "none" }}
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--kyra-mu)" }} />
          <Input
            data-testid="editor-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un texte..."
            className="pl-10"
            style={{ background: "#fff" }}
          />
        </div>
        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: "var(--kyra-li)" }}>
          <button
            data-testid="edit-lang-fr"
            onClick={() => setEditLang("fr")}
            className="px-4 py-2 text-xs font-medium cursor-pointer transition-colors"
            style={{ background: editLang === "fr" ? "var(--kyra-gr)" : "#fff", color: editLang === "fr" ? "#fff" : "var(--kyra-mu)", border: "none" }}
          >
            FR
          </button>
          <button
            data-testid="edit-lang-en"
            onClick={() => setEditLang("en")}
            className="px-4 py-2 text-xs font-medium cursor-pointer transition-colors"
            style={{ background: editLang === "en" ? "var(--kyra-gr)" : "#fff", color: editLang === "en" ? "#fff" : "var(--kyra-mu)", border: "none" }}
          >
            EN
          </button>
        </div>
      </div>

      {/* Sections */}
      <div className="flex flex-col gap-3">
        {SECTIONS.map((section) => {
          const keys = groupedKeys[section.key];
          if (!keys || keys.length === 0) return null;
          const isOpen = openSections[section.key];
          const sectionModified = keys.filter((k) => flatCustom[k] && flatCustom[k] !== "").length;

          return (
            <div key={section.key} className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--kyra-li)", background: "#fff" }}>
              <button
                data-testid={`section-${section.key}`}
                onClick={() => toggleSection(section.key)}
                className="flex items-center justify-between w-full px-5 py-4 cursor-pointer transition-colors hover:bg-gray-50"
                style={{ border: "none", background: "transparent" }}
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="w-4 h-4" style={{ color: "var(--kyra-mu)" }} /> : <ChevronRight className="w-4 h-4" style={{ color: "var(--kyra-mu)" }} />}
                  <span className="text-sm font-semibold">{section.label}</span>
                  <span className="text-[11px] font-light" style={{ color: "var(--kyra-mu)" }}>{keys.length} textes</span>
                </div>
                {sectionModified > 0 && (
                  <Badge className="text-[10px]" style={{ background: "rgba(34,197,94,0.1)", color: "var(--kyra-gm)", border: "none" }}>{sectionModified} modifie(s)</Badge>
                )}
              </button>
              {isOpen && (
                <div className="border-t" style={{ borderColor: "var(--kyra-li)" }}>
                  {keys.map((key) => {
                    const defaultVal = String(flatDefaults[key] || "");
                    const customVal = flatCustom[key] || "";
                    const isModified = customVal !== "" && customVal !== defaultVal;
                    const displayVal = customVal || "";
                    const isLong = defaultVal.length > 80;

                    return (
                      <div key={key} className="px-5 py-4 border-b last:border-b-0" style={{ borderColor: "rgba(0,0,0,0.04)" }}>
                        <div className="flex items-center gap-2 mb-2">
                          <code className="text-[10px] px-2 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.04)", color: "var(--kyra-mu)" }}>{key}</code>
                          {isModified && (
                            <Badge className="text-[9px] px-1.5 py-0" style={{ background: "rgba(34,197,94,0.1)", color: "var(--kyra-gm)", border: "none" }}>modifie</Badge>
                          )}
                          {isModified && (
                            <button onClick={() => handleReset(key)} className="p-0.5 rounded cursor-pointer hover:bg-gray-100" style={{ border: "none", background: "transparent" }} title="Remettre par defaut">
                              <RotateCcw className="w-3 h-3" style={{ color: "var(--kyra-mu)" }} />
                            </button>
                          )}
                        </div>
                        <div className="text-[11px] font-light mb-2 px-1 leading-relaxed" style={{ color: "var(--kyra-mu)" }}>
                          <span className="opacity-50">Defaut :</span> {defaultVal}
                        </div>
                        {isLong ? (
                          <Textarea
                            value={displayVal}
                            onChange={(e) => handleChange(key, e.target.value)}
                            placeholder={defaultVal}
                            className="text-sm min-h-[72px]"
                            style={{ background: isModified ? "rgba(34,197,94,0.03)" : "#fafafa", borderColor: isModified ? "rgba(34,197,94,0.2)" : "var(--kyra-li)" }}
                          />
                        ) : (
                          <Input
                            value={displayVal}
                            onChange={(e) => handleChange(key, e.target.value)}
                            placeholder={defaultVal}
                            className="text-sm"
                            style={{ background: isModified ? "rgba(34,197,94,0.03)" : "#fafafa", borderColor: isModified ? "rgba(34,197,94,0.2)" : "var(--kyra-li)" }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
